import { Alert } from "@opowiedzo/ui";
import { useCustom } from "@refinedev/core";

import { Badge } from "@/components/Badge";
import { Card, CardNote, DefinitionList, ErrorBox, Loading, PageHeader } from "@/components/Page";
import { EmptyRow, Table, Td, Th } from "@/components/Table";
import { formatDateTime } from "@/lib/format";

interface QueueDepth {
  name: string;
  depth: number;
}

interface JobFailure {
  area: string;
  label: string;
  count: number;
  last_at: string | null;
  last_error: string | null;
}

export interface IntegrationStatus {
  key: string;
  label: string;
  configured: boolean;
  detail: string;
}

export interface SystemState extends Record<string, unknown> {
  versions: {
    app: string;
    environment: string;
    python: string;
    database_revision: string | null;
  };
  broker_reachable: boolean;
  queues: QueueDepth[];
  job_failures: JobFailure[];
  integrations: IntegrationStatus[];
}

export function SystemPage() {
  const { query } = useCustom<SystemState>({ url: "/system", method: "get" });
  const dane = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!dane) return null;

  const awarie = dane.job_failures.filter((failure) => failure.count > 0);

  return (
    <>
      <PageHeader
        title="System"
        description="Czy to nasza wina. Kolejki, awarie w tle i stan integracji - bez wchodzenia na serwer."
      />

      {!dane.broker_reachable && (
        <div className="mb-4.5">
          <Alert tone="error">
            Redis jest nieosiągalny. Nic nie idzie w tle: ani pytania, ani transkrypcje, ani skład
            książek.
          </Alert>
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))] gap-4.5">
        <Card title="Kolejki">
          {dane.queues.length === 0 ? (
            <p className="text-base text-ink-soft">Brak odczytu z brokera.</p>
          ) : (
            <DefinitionList
              monoLabels
              rows={dane.queues.map((queue) => [
                queue.name,
                <span key={queue.name} className="tabular">
                  {queue.depth === 0 ? "pusto" : `${queue.depth} w kolejce`}
                </span>,
              ])}
            />
          )}
          <CardNote>
            Liczba to zadania czekające na workera. Rosnąca kolejka przy działającym brokerze znaczy
            zwykle, że worker nie wstał albo zawiesił się na jednym zadaniu.
          </CardNote>
        </Card>

        <Card title="Wersje">
          <DefinitionList
            rows={[
              ["Aplikacja", dane.versions.app],
              ["Środowisko", dane.versions.environment],
              ["Python", dane.versions.python],
              ["Schemat bazy", dane.versions.database_revision ?? "nieznany"],
            ]}
          />
          <CardNote>
            Rozbieżność między wersją schematu na serwerze a tą w repozytorium tłumaczy większość
            zgłoszeń "u mnie działa".
          </CardNote>
        </Card>
      </div>

      <div className="mt-4.5 grid gap-4.5">
        <Card title="Co się wywróciło w tle">
          {awarie.length === 0 ? (
            <p className="text-base text-ink-soft">Nic nie leży. Dobry dzień.</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Obszar</Th>
                  <Th align="right">Ile</Th>
                  <Th>Ostatnio</Th>
                  <Th>Powód</Th>
                </tr>
              </thead>
              <tbody>
                {awarie.map((failure) => (
                  <tr key={failure.area}>
                    <Td>{failure.label}</Td>
                    <Td align="right" numeric>
                      {failure.count}
                    </Td>
                    <Td nowrap>{formatDateTime(failure.last_at)}</Td>
                    <Td>
                      {/* Treść wyjątku czyta się jak kod, więc i wygląda jak kod. */}
                      <span className="font-mono text-sm break-words text-ink-soft">
                        {failure.last_error ?? "-"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        <Card title="Integracje">
          <Table>
            <thead>
              <tr>
                <Th>Usługa</Th>
                <Th>Stan</Th>
                <Th>Szczegóły</Th>
              </tr>
            </thead>
            <tbody>
              {dane.integrations.length === 0 && <EmptyRow colSpan={3}>Brak danych.</EmptyRow>}
              {dane.integrations.map((integration) => (
                <tr key={integration.key}>
                  <Td nowrap>{integration.label}</Td>
                  <Td>
                    <Badge tone={integration.configured ? "ok" : "warn"}>
                      {integration.configured ? "Skonfigurowana" : "Bez kluczy"}
                    </Badge>
                  </Td>
                  <Td>
                    <span className="text-ink-soft">{integration.detail}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <CardNote>
            Widać wyłącznie to, czy klucz jest ustawiony. Samych kluczy panel nie pokazuje i nie ma
            do nich dostępu.
          </CardNote>
        </Card>
      </div>
    </>
  );
}
